import React, { Component, ErrorInfo, ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { trackException } from "utils/amplitudeAnalytics";

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    trackException(error, {
      source: "react.errorBoundary",
      componentStack: errorInfo.componentStack,
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            px: 2,
          }}
        >
          <Typography component="h1" variant="h6">
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            An unexpected error occurred. Please refresh the page and try again.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>
            Refresh Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
